import React, { FC, useEffect, useState, memo, useCallback, useMemo, useRef } from 'react';

import { Checkbox, InputNumber, Space, Tooltip } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import classNames from 'classnames';

import { finishRows, buildDisplayGroupings, resolveGroupAxisFilter } from '../../../../../../helpers/utils/reports';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

interface GroupHeaderProps {
    sort: Record<string, 'ASC' | 'DESC'>;
    dataKey: string;
    hideSelectionControls?: boolean;
    blockId?: string;
    isModal?: boolean;
}

interface GroupingItem {
    label?: string;
    name?: string;
    dataKey?: string;
    [key: string]: unknown;
}

interface TableDataItem {
    id?: string;
    pathStr?: string;
    pathStr2?: string;
    group?: string;
    [key: string]: unknown;
}

const GroupHeader: FC<GroupHeaderProps> = ({ sort, dataKey, hideSelectionControls = false, blockId, isModal = false }) => {

    const [ checked, setChecked ] = useState(true);

    const {
        groupings,
        groupsChecked,
        totalChecked,
        tableData,
        percentages,
        filtersTable,
        sortTable,
        groupsSelectionMode,
        selectFirstNRows
    } = useTypedSelector((state) =>
        blockId ? (state.table.blocks[blockId] ?? state.table) : state.table
    );

    // Фильтр по группам регистраций того же скоупа, что и groupings: приводит ось
    // к фильтру (уровень-группа не по фильтру выкидывается) — иначе заголовок уровня
    // съезжал бы на не-групповые строки.
    const { filters } = useTypedSelector((state) => {
        if (blockId) return state.filter.blocks[blockId] ?? { filters: [] };
        if (isModal) return state.filter.dashboardModal;
        return state.filter;
    });
    const groupFilter = useMemo(() => resolveGroupAxisFilter(filters), [filters]);

    // Схлопнутый список уровней для заголовка: набор групп регистрации
    // показываем одним именем «Группы регистраций», а не перечислением групп.
    const displayGroupings = useMemo(() => buildDisplayGroupings(groupings, groupFilter), [groupings, groupFilter]);
    const { 
        clearTotalChecked, 
        setGroupingsChecked, 
        setSortTable, 
        setMetaChartFetching, 
        setMetaCompareChartFetching,
        setGroupsSelectionMode,
        setSelectFirstNRows
    } = useActions();

    useEffect(() => {
        if (groupsChecked.length === 0 && totalChecked.length === 0) {
            setChecked(false);
        } else {
            setChecked(true);
        }
    }, [ groupsChecked, totalChecked ]);

    // Получаем отфильтрованные данные для правильного подсчёта строк
    const filteredData = useMemo(() => {
        try {
            return finishRows({
                rows: tableData.data,
                filters: filtersTable,
                sort: sortTable,
                percentages,
                totalRow: tableData.total,
            });
        } catch (e) {
            return [];
        }
    }, [tableData.data, filtersTable, sortTable, percentages, tableData.total]);

    // Проверка, выбраны ли все строки
    const isAllSelected = useMemo(() => {
        return groupsSelectionMode === 'all';
    }, [groupsSelectionMode]);

    // Проверка режима "первые N строк"
    const isFirstNSelected = useMemo(() => {
        return groupsSelectionMode === 'firstN';
    }, [groupsSelectionMode]);

    // Функция очистки выбранных групп - определяем первой, чтобы использовать в других функциях
    const clearCheckedGroups = useCallback(() => {
        clearTotalChecked();
        setGroupingsChecked([]);
        setGroupsSelectionMode('manual');
        setSelectFirstNRows(null);
    }, [clearTotalChecked, setGroupingsChecked, setGroupsSelectionMode, setSelectFirstNRows]);

    // Фильтруем данные, исключая строки "Итого и среднее"
    const groupingRowsOnly = useMemo(() => {
        return filteredData.filter((item: TableDataItem) => 
            item.pathStr !== 'total' && item.pathStr !== 'total_choosen'
        );
    }, [filteredData]);

    // Ref для отслеживания предыдущего количества выбранных строк
    // Сбрасываем режим только если пользователь ВРУЧНУЮ снял галочку (т.е. количество уменьшилось)
    const prevGroupsCheckedLengthRef = useRef<number>(groupsChecked.length);
    // Ref для отслеживания программного изменения (переход между режимами)
    const isProgrammaticChangeRef = useRef<boolean>(false);
    
    useEffect(() => {
        const prevLength = prevGroupsCheckedLengthRef.current;
        const currentLength = groupsChecked.length;
        
        // Обновляем ref
        prevGroupsCheckedLengthRef.current = currentLength;
        
        // Если это программное изменение (переход между режимами), пропускаем сброс
        if (isProgrammaticChangeRef.current) {
            isProgrammaticChangeRef.current = false;
            return;
        }
        
        // Сбрасываем режим только если количество УМЕНЬШИЛОСЬ (пользователь снял галочку вручную)
        // и данные уже загружены (groupingRowsOnly.length > 0)
        if (currentLength < prevLength && groupingRowsOnly.length > 0) {
            if (groupsSelectionMode === 'all' || groupsSelectionMode === 'firstN') {
                setGroupsSelectionMode('manual');
                setSelectFirstNRows(null);
            }
        }
    }, [groupsChecked.length, groupingRowsOnly.length, groupsSelectionMode, setGroupsSelectionMode, setSelectFirstNRows]);

    // Функция выбора всех строк
    const selectAllRows = useCallback(() => {
        try {
            const allGroupings = groupingRowsOnly.map((item: TableDataItem) => ({
                ids: (item as any).clientData?.path || [item.id || item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));

            clearTotalChecked();
            setGroupingsChecked(allGroupings);
            setGroupsSelectionMode('all');
            setSelectFirstNRows(null);
            
            setMetaChartFetching({ value: true });
            setMetaCompareChartFetching({ value: true });
        } catch (e) {
            console.error(e);
        }
    }, [groupingRowsOnly, clearTotalChecked, setGroupingsChecked, setGroupsSelectionMode, setSelectFirstNRows, setMetaChartFetching, setMetaCompareChartFetching]);

    // Функция выбора первых N строк
    const selectFirstN = useCallback((n: number | null) => {
        if (n === null || n <= 0) {
            clearCheckedGroups();
            return;
        }

        try {
            const firstNGroupings = groupingRowsOnly.slice(0, n).map((item: TableDataItem) => ({
                ids: (item as any).clientData?.path || [item.id || item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));

            clearTotalChecked();
            setGroupingsChecked(firstNGroupings);
            setGroupsSelectionMode('firstN');
            setSelectFirstNRows(n);
            
            setMetaChartFetching({ value: true });
            setMetaCompareChartFetching({ value: true });
        } catch (e) {
            console.error(e);
        }
    }, [groupingRowsOnly, clearTotalChecked, setGroupingsChecked, clearCheckedGroups, setGroupsSelectionMode, setSelectFirstNRows, setMetaChartFetching, setMetaCompareChartFetching]);

    // Обработчик изменения чекбокса "Выбрать все"
    const onSelectAllChange = useCallback((e: CheckboxChangeEvent) => {
        e.stopPropagation();
        if (groupings.length <= 0) return;

        if (isAllSelected) {
            // Снимаем выбор всех
            clearCheckedGroups();
        } else {
            // Выбираем все
            selectAllRows();
        }
    }, [groupings.length, isAllSelected, selectAllRows, clearCheckedGroups]);

    // Обработчик изменения поля "Первые N строк"
    const onFirstNChange = useCallback((value: number | null) => {
        // Сначала всегда обновляем значение в Redux для отображения в UI
        setSelectFirstNRows(value);
        
        // Затем применяем выбор строк
        if (value === null || value <= 0) {
            // Очищаем выбор, но НЕ вызываем clearCheckedGroups, т.к. он сбросит selectFirstNRows
            clearTotalChecked();
            setGroupingsChecked([]);
            setGroupsSelectionMode('manual');
            return;
        }

        try {
            const firstNGroupings = groupingRowsOnly.slice(0, value).map((item: TableDataItem) => ({
                ids: (item as any).clientData?.path || [item.id || item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));

            // Помечаем как программное изменение, чтобы useEffect не сбросил режим
            // (это важно при переходе из режима 'all' в 'firstN')
            isProgrammaticChangeRef.current = true;
            
            clearTotalChecked();
            setGroupingsChecked(firstNGroupings);
            setGroupsSelectionMode('firstN');
            
            setMetaChartFetching({ value: true });
            setMetaCompareChartFetching({ value: true });
        } catch (e) {
            console.error(e);
        }
    }, [groupingRowsOnly, clearTotalChecked, setGroupingsChecked, setGroupsSelectionMode, setSelectFirstNRows, setMetaChartFetching, setMetaCompareChartFetching]);

    const setCheckedForCurrentGroup = () => {
        try {
            const data = finishRows({
                rows: tableData.data,
                filters: filtersTable,
                sort: sortTable,
                percentages,
                totalRow: tableData.total,
            });

            const firstGroupings = data.slice().map((item: TableDataItem) => ({
                ids: (item as any).clientData?.path || [item.id || item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));


            clearTotalChecked();
            setGroupingsChecked(firstGroupings);
        } catch (e) {
            console.error(e);
        }
    };

    const onChange = useCallback((e: CheckboxChangeEvent) => {
        e.stopPropagation();
        if (groupings.length <= 0) {
            setChecked(false);
            return;
        }

        if (checked) {
            setChecked(false);
            clearCheckedGroups();

            setMetaChartFetching({ value: true });
            setMetaCompareChartFetching({ value: true });
        } else {
            setChecked(true);
            setCheckedForCurrentGroup();

            setMetaChartFetching({ value: true });
            setMetaCompareChartFetching({ value: true });
        }
    }, [checked, groupings.length, clearCheckedGroups, setCheckedForCurrentGroup, setMetaChartFetching, setMetaCompareChartFetching]);

    const sortGroups = useCallback(() => {
        let newSort: Record<string, 'ASC' | 'DESC'> = {};

        if (sort[dataKey] === 'ASC') {
            newSort[dataKey] = 'DESC';
        } else {
            newSort[dataKey] = 'ASC';
        }

        if (sort[dataKey] === 'DESC') {
            if (blockId) {
                setSortTable({ data: {}, blockId });
            } else {
                setSortTable({});
            }
        } else {
            if (blockId) {
                setSortTable({ data: newSort, blockId });
            } else {
                setSortTable(newSort);
            }
        }
    }, [dataKey, sort, setSortTable, blockId]);

    return (
        <div style={{ marginLeft: (blockId || isModal) ? -6 : -18 }}>
            <div
                className={classNames('table-header-sort', 'table-header-sort-right', 'table-header-withIcon', {
                    'sort-asc': sort?.[dataKey] === 'ASC',
                    'sort-desc': sort?.[dataKey] === 'DESC',
                })}
            >
                <span 
                    style={{ 
                        cursor: 'pointer', 
                        marginLeft: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.4',
                        maxHeight: '4.2em'
                    } as React.CSSProperties} 
                    onClick={sortGroups}
                    title={displayGroupings.length > 0 ? displayGroupings.filter(Boolean).map((item: GroupingItem) => item?.label || item?.name || item?.dataKey).filter(Boolean).join(' → ') : undefined}
                >
                    {
                        displayGroupings.length > 0 ?
                            displayGroupings.filter(Boolean).map((item: GroupingItem) => item?.label || item?.name || item?.dataKey).filter(Boolean).join(' → ') :
                            'Группировки не выбраны'
                    }
                </span>
            </div>
            {/* Дополнительные опции выбора строк */}
            {groupings.length > 0 && !hideSelectionControls && (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 20, 
                    marginTop: 8,
                    marginLeft: 0,
                    fontSize: 12,
                    color: '#595959'
                }}>
                    <Tooltip title="При выборе будут отмечены все доступные строки в таблице. Настройка сохраняется с отчётом.">
                        <Checkbox 
                            checked={isAllSelected} 
                            onChange={onSelectAllChange}
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <span style={{ fontSize: 12, color: '#595959', lineHeight: '22px' }}>Выбрать все</span>
                        </Checkbox>
                    </Tooltip>
                    <Tooltip title="Введите число — будут отмечены первые N строк таблицы. Настройка сохраняется с отчётом.">
                        <Space size={6} style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: '#595959', lineHeight: '22px' }}>Первые</span>
                            <InputNumber
                                size="small"
                                min={1}
                                max={groupingRowsOnly.length || 1000}
                                value={selectFirstNRows}
                                onChange={onFirstNChange}
                                placeholder="N"
                                controls={false}
                                style={{ width: 38, height: 22 }}
                            />
                        </Space>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

export default memo(GroupHeader);
